declare global {
  interface Window {
    // Swiper is exposed globally by src/assets/2022/js/swiper.js
    Swiper?: any;
  }
}

type CleanupFn = () => void;

const safeSetDisplay = (el: HTMLElement, display: 'none' | 'block') => {
  el.style.display = display;
};

const initMenuAndLanguage = (): CleanupFn => {
  const menuTrigger = document.getElementById('menu-trigger');
  const navMenuUl = document.getElementById('navmenuul');
  const menuIcon = document.querySelector<HTMLElement>('.menu-icon');
  const langEl = document.getElementById('Lang') as HTMLAnchorElement | null;
  if (!menuTrigger || !navMenuUl || !menuIcon || !langEl) return () => {};

  const updateLanguageHref = () => {
    const cate = document.body.getAttribute('cate') || '';
    const lang = document.body.getAttribute('lang') || '';
    const hash = window.location.hash || '';

    // Build clean routes without .php, preserve hash
    if (lang === 'en') {
      const target = cate === 'index' ? '/2022/zh' : `/2022/zh/${cate}`;
      langEl.setAttribute('href', `${target}${hash}`);
    } else if (lang === 'zh') {
      const target = cate === 'index' ? '/2022' : `/2022/${cate}`;
      langEl.setAttribute('href', `${target}${hash}`);
    }
  };

  const onResize = () => {
    const innerWidth = window.innerWidth;
    if (innerWidth <= 1220) {
      menuTrigger.classList.add('show');
      if (menuIcon.classList.contains('change')) safeSetDisplay(navMenuUl, 'block');
      else safeSetDisplay(navMenuUl, 'none');
    } else {
      menuTrigger.classList.remove('show');
      safeSetDisplay(navMenuUl, 'block');
      menuIcon.classList.remove('change');
    }
  };

  const onMenuClick = () => {
    const currentDisplay = window.getComputedStyle(navMenuUl).display;
    if (currentDisplay === 'none') {
      safeSetDisplay(navMenuUl, 'block');
      menuIcon.classList.add('change');
    } else {
      safeSetDisplay(navMenuUl, 'none');
      menuIcon.classList.remove('change');
    }
  };

  // Initial setup (matches reference controller.js logic)
  updateLanguageHref();
  onResize();
  if (window.innerWidth < 1220) menuTrigger.classList.add('show');
  else menuTrigger.classList.remove('show');

  window.addEventListener('resize', onResize);
  menuTrigger.addEventListener('click', onMenuClick);
  window.addEventListener('hashchange', updateLanguageHref);

  return () => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('hashchange', updateLanguageHref);
    menuTrigger.removeEventListener('click', onMenuClick);
  };
};

const initWinnersToggle = (): CleanupFn => {
  // winners.php renders category wrappers with class like `Winners_ArtsandCulture`
  // and details containers with class like `Winners_ArtsandCulture_Details`.
  const listeners: Array<{ el: HTMLElement; handler: EventListener }> = [];

  const divs = Array.from(document.querySelectorAll('div[class]')) as HTMLElement[];
  divs.forEach((div) => {
    const classAttr = div.getAttribute('class') || '';
    const classes = classAttr.split(' ').filter(Boolean);
    const categoryClass = classes.find((c) => c.startsWith('Winners_') && !c.endsWith('_Details'));
    if (!categoryClass) return;

    const handler: EventListener = () => {
      const detailsEls = Array.from(
        document.querySelectorAll<HTMLElement>(`.${categoryClass}_Details`)
      );
      detailsEls.forEach((detailsEl) => {
        const current = window.getComputedStyle(detailsEl).display;
        if (current === 'none') safeSetDisplay(detailsEl, 'block');
        else safeSetDisplay(detailsEl, 'none');
      });
    };

    div.addEventListener('click', handler);
    listeners.push({ el: div, handler });
  });

  return () => {
    listeners.forEach(({ el, handler }) => el.removeEventListener('click', handler));
  };
};

const initAwardsSwiper = (): CleanupFn => {
  const swiperCtor = window.Swiper;
  if (!swiperCtor) return () => {};

  const mySwiper = document.querySelector('.mySwiper');
  const mySwiper2 = document.querySelector('.mySwiper2');
  if (!mySwiper || !mySwiper2) return () => {};

  const params = new URLSearchParams(window.location.search);
  const rawCatId = params.get('cat_id') ?? params.get('CateID') ?? params.get('catID');
  const catId = rawCatId != null ? Number(rawCatId) : NaN;

  let thumbsSwiper: any | undefined;
  let mainSwiper: any | undefined;

  try {
    thumbsSwiper = new swiperCtor('.mySwiper', {
      loop: true,
      spaceBetween: 10,
      slidesPerView: 3,
      freeMode: true,
      watchSlidesProgress: true,
    });

    mainSwiper = new swiperCtor('.mySwiper2', {
      autoplay: { delay: 5000, disableOnInteraction: false },
      loop: true,
      spaceBetween: 10,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      thumbs: { swiper: thumbsSwiper },
    });

    if (!Number.isNaN(catId)) mainSwiper.slideTo(catId);
  } catch {
    // Swiper is optional; if initialization fails, we just skip it.
  }

  return () => {
    try {
      mainSwiper?.destroy?.(true, true);
    } catch {
      // ignore
    }
    try {
      thumbsSwiper?.destroy?.(true, true);
    } catch {
      // ignore
    }
  };
};

export const mount2022Runtime = (): CleanupFn => {
  const cleanups: CleanupFn[] = [];
  cleanups.push(initMenuAndLanguage());
  // Do not auto-open popup/Lightbox in 2022
  cleanups.push(initWinnersToggle());
  cleanups.push(initAwardsSwiper());

  return () => {
    cleanups.forEach((fn) => fn());
  };
};

